import { React, useState, useEffect } from 'react';
import { Flex, Box, Text, Button, Icon, IconButton, Checkbox } from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select } from 'chakra-react-select';

import { AiFillMinusCircle } from 'react-icons/ai';
import { IoTrashOutline } from 'react-icons/io5';

import { metadataOptions, selectStyles, experienceLevelStyles } from '../../helpers/chakraSelect';
import { useDispatch } from '../../config/store';
import GreenTitleBox from './GreenTitleBox';

function StrongestTechStack({ interview, experienceMetadata: uncuratedExperienceMetadata, checklist }) {
  // Clone original array to mutate it. And if array is empty, add at least 1 row of Strongest Tech Stack
  // Also for now check and grab from the old field Tech_stack until migrated
  const curatedSeniorityLevelByTech = () => {
    let curatedArray = [];
    if (interview.interviewData.Seniority_Level_By_Tech?.length !== 0)
      curatedArray = interview.interviewData.Seniority_Level_By_Tech;
    else if (interview.interviewData.Tech_stack?.length !== 0)
      interview.interviewData.Tech_stack.map(stack => curatedArray.push({ Tech: stack, Experience: '' }));
    return curatedArray ? curatedArray.filter(item => item.Tech !== '') : [];
  };

  const curatedExperienceMetadata = () => {
    const expMetadata = metadataOptions(uncuratedExperienceMetadata, 'Experience');

    // Remove -None- as an Experience option
    const filteredCuratedExperienceMetadata = expMetadata.filter(exp => !exp.value.includes('None'));

    // Add colors to each Experience option
    const addStyleToExperience = (exp, color, bgColor) => {
      const experience = filteredCuratedExperienceMetadata.find(e => e.value.startsWith(exp));
      if (experience) experience.colors = { color, bgColor };
    };
    addStyleToExperience('Beginner', '#3B4B53', 'transparent');
    addStyleToExperience('Experienced', '#337bff', 'transparent');
    addStyleToExperience('Expert', '#38A75D', 'transparent');

    return filteredCuratedExperienceMetadata;
  };

  const [seniorityLevelByTech, setSeniorityLevelByTech] = useState(curatedSeniorityLevelByTech());
  const [experienceMetadata, _setExperienceMetadata] = useState(curatedExperienceMetadata());
  const methods = useFormContext();
  const dispatch = useDispatch();
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckbox = () => {
    setIsChecked(!isChecked);
  };

  useEffect(() => {
    dispatch({
      type: 'SET_CHECKED',
      nonTechCheckbox: !isChecked,
    });
  }, [isChecked]);

  useEffect(() => setSeniorityLevelByTech(curatedSeniorityLevelByTech()), [interview]);

  return (
    <>
      <GreenTitleBox
        title={`${checklist.length > 0 ? '8' : '7'}. Strongest Technology Stack:`}
        helpText='Explain which are the programming languages, frameworks, etc. the candidate knows and list them indicating the level of experience for each, considering both theory knowledge and years of work experience.'
      />
      <Flex gap='12' mb='8px'>
        <Flex flex='2' flexDirection='column'>
          {seniorityLevelByTech?.length > 0 && (
            <Flex justifyContent='space-between' alignItems='center' mb='12px'>
              <Box mr='48px'>
                <Text>Programming Language / Framework</Text>
              </Box>
              <Flex alignItems='center'>
                <Text mr='24px' minWidth='425px'>
                  Experience level
                </Text>
                <Icon as={AiFillMinusCircle} boxSize={10} color='gray.200' visibility='hidden' />
              </Flex>
            </Flex>
          )}

          {seniorityLevelByTech.map(({ Tech, Experience }, index) => (
            <Flex justifyContent='space-between' alignItems='center' mb='18px' key={`${Tech}-${Experience}`}>
              <Box mr='48px' minWidth='290px'>
                <Controller
                  name='Seniority_Level_By_Tech'
                  control={methods.control}
                  render={({ field: { onChange, _value } }) => {
                    const changeTechnology = selectedOption => {
                      const newArr = seniorityLevelByTech.map(object => {
                        if (object.Tech === seniorityLevelByTech[index].Tech) {
                          return { ...object, Tech: selectedOption.value };
                        }
                        return object;
                      });
                      setSeniorityLevelByTech(newArr);
                      onChange(newArr);
                    };

                    const filteredMetadataOptions = () => {
                      const options = metadataOptions(uncuratedExperienceMetadata, 'Tech');
                      const newArr = seniorityLevelByTech.map(object => object.Tech);
                      const filteredOptions = options.filter(
                        tech => !newArr.includes(tech.value) && tech.value !== '-None-',
                      );
                      return filteredOptions;
                    };

                    return (
                      <Select
                        name='Technology'
                        options={filteredMetadataOptions()}
                        selectedOptionStyle='check'
                        closeMenuOnSelect
                        chakraStyles={selectStyles}
                        onChange={changeTechnology}
                        value={{
                          value: seniorityLevelByTech[index].Tech,
                          label: seniorityLevelByTech[index].Tech,
                        }}
                      />
                    );
                  }}
                />
              </Box>
              <Flex alignItems='center'>
                <Box mr='24px' minWidth='425px'>
                  <Controller
                    name='Seniority_Level_By_Tech'
                    control={methods.control}
                    render={({ field: { onChange, _value } }) => {
                      // Match colors with current value
                      const experienceLevelColors = experienceMetadata.find(
                        object => object.value === seniorityLevelByTech[index].Experience,
                      )?.colors;

                      const changeExperience = selectedOption => {
                        const newArr = seniorityLevelByTech.map(object => {
                          if (object.Tech === seniorityLevelByTech[index].Tech) {
                            return { ...object, Experience: selectedOption.value };
                          }
                          return object;
                        });
                        setSeniorityLevelByTech(newArr);
                        onChange(newArr);
                      };

                      return (
                        <Select
                          isSingle
                          name='Experience Level'
                          options={experienceMetadata}
                          selectedOptionStyle='check'
                          closeMenuOnSelect
                          chakraStyles={experienceLevelStyles}
                          onChange={changeExperience}
                          value={{
                            value: seniorityLevelByTech[index].Experience,
                            label: seniorityLevelByTech[index].Experience,
                            colors: experienceLevelColors,
                          }}
                        />
                      );
                    }}
                  />
                </Box>
                <Controller
                  name='Seniority_Level_By_Tech'
                  control={methods.control}
                  render={({ field: { onChange, _value } }) => {
                    const removeTech = () => {
                      const newArr = seniorityLevelByTech.filter(i => i !== seniorityLevelByTech[index]);
                      setSeniorityLevelByTech(newArr);
                      onChange(newArr);
                    };

                    return (
                      <IconButton
                        size='sm'
                        height='38px'
                        width='38px'
                        color='gray.500'
                        icon={<IoTrashOutline fontSize='20px' />}
                        onClick={removeTech}
                      />
                    );
                  }}
                />
              </Flex>
            </Flex>
          ))}

          <Box>
            <Controller
              name='Seniority_Level_By_Tech'
              control={methods.control}
              render={({ field: { onChange, _value } }) => {
                const emptyTechRow = seniorityLevelByTech.find(row => row.Tech === '');

                const addTech = () => {
                  // Prevent adding more than 1 new row of Strongest Tech Stack
                  if (emptyTechRow) return;

                  const newArr = [...seniorityLevelByTech, { Tech: '', Experience: '' }];
                  setSeniorityLevelByTech(newArr);
                  onChange(newArr);
                };

                return (
                  <Flex>
                    <Button
                      size='md'
                      colorScheme='blue'
                      variant='outline'
                      color='blue.500'
                      onClick={addTech}
                      disable={emptyTechRow}
                      maxWidth='136px'
                      letterSpacing='0px'
                      paddingX='10px'>
                      Add New Stack
                    </Button>
                    <Checkbox isChecked={isChecked} onChange={handleCheckbox} mr={2} marginLeft='20px'>
                      <Text fontSize='12px'>Non-tech position</Text>
                    </Checkbox>
                  </Flex>
                );
              }}
            />
          </Box>
        </Flex>
        <Flex flex='2' />
      </Flex>
    </>
  );
}

export default StrongestTechStack;
